'use client';

import { FooterLink, FooterLinksStack } from '@/app/footer/links/footer-links-data';
import { CinemarkLink } from '@/components/cinemark-link/cinemark-link';
import { useDataAnalytics } from '@/hooks/analytics/analytics';
import { dataTestTransformer } from '@/utils/data-test-helper';
import clsx from 'clsx';
import React, { useState } from 'react';
import accodionStyles from './accordion.module.scss';

type AccordionProps = FooterLinksStack;

export const Accordion = ({ headerText, links }: AccordionProps): React.JSX.Element => {
  const [accordionOpen, setAccordionOpen] = useState(false);
  const { trackClick } = useDataAnalytics();
  const linkClassName = 'flex py-sm px-[15px] text-sm items-center';

  const externalLinkIcon = (link: FooterLink): React.JSX.Element | null => {
    if (!link.externalLink) return null;

    return <>
      <>&nbsp;</>
      <svg aria-label={`${link.text} external link icon`}
        className="icon-external-link icon-default dont-grow"
        role="img"
      >
        <use href="#externalsite" />
      </svg>
    </>;
  };

  const handleFooterButtonClick = (link: FooterLink): void => {
    trackClick({ linkCategory: link.dataLinkCategory, linkName: link.dataLinkName });
    link.onClick?.();
  };

  return (
    <div>
      <h3 className={clsx('mb-2', accodionStyles['accorion-item-text'])}
        data-test={`footer-links-header-${dataTestTransformer(headerText)}`}
      >
        {headerText}
      </h3>
      <button className={clsx('flex justify-between w-full py-sm px-[15px] border-b border-b-gray-100', accodionStyles['accorion-item-button'],
        accordionOpen ? 'bg-white font-bold' : 'bg-[#cccccc] font-normal')}
      data-test={`footer-link-toggle-${dataTestTransformer(headerText)}`}
      type="button"
      onClick={() => setAccordionOpen(!accordionOpen)}
      >
        <span>{headerText}</span>
        <span>{accordionOpen ? '-' : '+'}</span>
      </button>
      <div className={clsx('grid transition-all duration-300 ease-in-out', accordionOpen ? `${accodionStyles.open}` : '',
        accodionStyles['accorion-item-content'])}
      >
        <ul className="overflow-hidden bg-gray-100"
          data-test={`footer-links-${dataTestTransformer(headerText)}`}
        >
          {links.map((link: FooterLink, index: number) =>
            <li key={index}>
              {!!link.onClick &&
                <button className={clsx(linkClassName, link.className)}
                  data-test={`button-footer-link-${dataTestTransformer(link.text)}`}
                  onClick={() => handleFooterButtonClick(link)}
                >
                  {link.text}
                  {externalLinkIcon(link)}
                </button>
              }

              {!link.onClick &&
                <CinemarkLink className={clsx(linkClassName, link.className)}
                  dataLinkCategory={link.dataLinkCategory}
                  dataLinkName={link.dataLinkName}
                  dataTest={`link-footer-link-${dataTestTransformer(link.text)}`}
                  href={link.href}
                  rel="noreferrer"
                  target={link.externalLink ? '_blank' : '_self'}
                  useAnchor={link.useAnchor}
                >
                  {link.text}
                  {externalLinkIcon(link)}
                </CinemarkLink>
              }
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};
